"use client";

import Link from "next/link";
import { useLang } from "@/components/LangProvider";

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function NoteIcon({ className }: { className?: string }) {
  // eslint-disable-next-line @next/next/no-img-element
  return <img src="/v2/icon/note_sub.svg" alt="note" className={className} />;
}

function CartIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
    </svg>
  );
}

// Mirrors the header nav exactly (same items, same order, same destinations)
type FooterLink = {
  key: "brand" | "agriculture" | "news" | "company" | "contact";
  href: string;
};

const footerLinks: FooterLink[] = [
  { key: "brand", href: "/#brand" },
  { key: "agriculture", href: "/#agriculture" },
  { key: "news", href: "/#news" },
  { key: "company", href: "/#company" },
  { key: "contact", href: "/contact" },
];

export function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-paper text-foreground border-t border-rule pt-24 pb-8">
      <div className="mx-auto max-w-site px-6 md:px-14 flex flex-col items-center gap-7">
        <Link href="/" className="opacity-85 hover:opacity-100 transition-opacity duration-200" aria-label="Plum Town">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/logo_tr.svg" alt="Plum Town" className="h-[38px] w-auto" />
        </Link>

        <nav className="flex flex-wrap justify-center gap-8 mt-3">
          {footerLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="font-sans font-medium uppercase text-[#4a4a4a] hover:text-accent-forest transition-colors duration-200"
              style={{ fontSize: 11, letterSpacing: "0.2em" }}
            >
              {t.nav[link.key]}
            </Link>
          ))}
        </nav>

        <div className="w-full max-w-[760px] h-px bg-rule mt-2" />

        <div className="w-full max-w-[760px] grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-4 mt-1">
          <div
            className="font-mono text-mute text-center md:text-left md:justify-self-start"
            style={{ fontSize: 10.5, letterSpacing: "0.18em" }}
          >
            {t.footer.address}
          </div>

          <div className="flex justify-center gap-4 md:justify-self-center">
            <a
              href="https://note.com/plumtown"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="note"
              className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-foreground/[0.06] text-[#4a4a4a] hover:bg-accent-forest/15 hover:text-accent-forest hover:-translate-y-px transition-all duration-200"
            >
              <NoteIcon className="w-[18px] h-[18px]" />
            </a>
            <a
              href="https://www.instagram.com/soga_craft_umeshu/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-foreground/[0.06] text-[#4a4a4a] hover:bg-accent-forest/15 hover:text-accent-forest hover:-translate-y-px transition-all duration-200"
            >
              <InstagramIcon className="w-[18px] h-[18px]" />
            </a>
            <a
              href="https://shop.plum-town.jp/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t.shopButton}
              className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-foreground/[0.06] text-[#4a4a4a] hover:bg-accent-forest/15 hover:text-accent-forest hover:-translate-y-px transition-all duration-200"
            >
              <CartIcon className="w-[18px] h-[18px]" />
            </a>
          </div>

          <div
            className="font-sans text-mute text-center md:text-right md:justify-self-end"
            style={{ fontSize: 10, letterSpacing: "0.24em" }}
          >
            {t.footer.copyright}
          </div>
        </div>

        {/* 上の nav はヘッダーと同じ項目に揃えてある。法務ページはヘッダーに
            出さないので、その外側に置く */}
        <Link
          href="/privacy-policy"
          className="font-sans font-medium uppercase text-mute hover:text-accent-forest transition-colors duration-200 mt-1"
          style={{ fontSize: 10, letterSpacing: "0.2em" }}
        >
          {t.footer.privacy}
        </Link>
      </div>
    </footer>
  );
}
